import { ArrowRight, ExternalLink, MessageSquare, Search } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useStockholmEventTracking,
  type StockholmSourceSurface,
} from "../lib/analytics";
import { CAIRE_CONTACT_URL } from "../lib/site";

/** Synthetic GA path for outbound caire.se/kontakt (never send full URLs). */
export const OUTBOUND_CAIRE_CONTACT_PATH = "/outbound/caire-kontakt";

export interface ConversionCtaBandProps {
  /** Page/surface that rendered the band (GA `source_surface`). */
  sourceSurface: StockholmSourceSurface;
  /** Optional district-scoped finder path, e.g. `/stadsdelar/kungsholmen`. */
  primaryHref?: string;
  primaryLabel?: string;
  /** When false, omit the primary finder CTA (e.g. already on `/hitta-hemtjanst`). */
  showPrimary?: boolean;
  title?: string;
  description?: string;
}

/**
 * Bottom-of-page conversion band: find providers (path to generate_lead) or
 * contact Caire. Fires seo_funnel_click; do not mark that as a GA4 key event.
 *
 * @param props.sourceSurface - Analytics surface for the click
 * @param props.primaryHref - Primary CTA path (default `/hitta-hemtjanst`)
 * @param props.primaryLabel - Primary CTA label
 * @param props.showPrimary - Whether to render the primary finder CTA
 * @returns Conversion CTA section element
 */
export function ConversionCtaBand({
  sourceSurface,
  primaryHref = "/hitta-hemtjanst",
  primaryLabel = "Hitta hemtjänst",
  showPrimary = true,
  title = "Nästa steg: kontakta utförare eller oss",
  description = "Öppna en utförarprofil och skicka en förfrågan, eller kontakta Caire om du har frågor om guiden.",
}: ConversionCtaBandProps) {
  const track = useStockholmEventTracking();

  return (
    <section
      className="mt-10 rounded-3xl bg-brand-primary p-7 text-white"
      aria-labelledby="conversion-cta-heading"
      data-testid="conversion-cta-band"
    >
      <h2 id="conversion-cta-heading" className="text-2xl font-bold">
        {title}
      </h2>
      <p className="mt-2 max-w-3xl text-white/85">{description}</p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        {showPrimary ? (
          <Link
            to={primaryHref}
            data-testid="conversion-cta-finder"
            onClick={() =>
              track("seo_funnel_click", {
                source_surface: sourceSurface,
                target_path: primaryHref.split("?")[0] || primaryHref,
              })
            }
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            {primaryLabel}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : null}
        <Link
          to="/guider/valja-hemtjanst"
          data-testid="conversion-cta-guide"
          onClick={() =>
            track("seo_funnel_click", {
              source_surface: sourceSurface,
              target_path: "/guider/valja-hemtjanst",
            })
          }
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/50 px-5 py-3 font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          Så kontaktar du utförare
        </Link>
        <a
          href={CAIRE_CONTACT_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="conversion-cta-caire"
          onClick={() =>
            track("seo_funnel_click", {
              source_surface: sourceSurface,
              target_path: OUTBOUND_CAIRE_CONTACT_PATH,
            })
          }
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/50 px-5 py-3 font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          Kontakta Caire
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}
